import { useCallback, useEffect, useRef, useState } from "react";
import { fireGiftEffect } from "@/lib/giftEffects";
import GiftAnimation, { isAnimatedGift } from "@/components/GiftAnimation";
import { useLiveRoomSocket } from "@/hooks/useLiveRoomWS";
import UserBadge from "@/components/UserBadge";

/**
 * Listens to `gift:sent` on the shared room WebSocket. When the same sender fires
 * multiple gifts within a rolling `COMBO_WINDOW_MS` window, they coalesce into
 * one animated combo card whose `×N` counter increments with every hit
 * (Twitch/Fanbase style). The card only fades out `COMBO_WINDOW_MS` after
 * the LAST hit.
 *
 * A big center emoji burst still fires on every incoming gift so the whole
 * room feels each new hit.
 */
const COMBO_WINDOW_MS = 6000;

export default function LiveGiftOverlay({ streamId, silent = false }) {
  const silentRef = useRef(silent);
  useEffect(() => { silentRef.current = silent; }, [silent]);
  // Combos are keyed by `${sender_handle}|${gift_key}`.
  // Each combo: { key, count, coins, emoji, label, sender_handle, lane, lastAt }
  const [combos, setCombos] = useState({});
  // One-shot center bursts triggered by newest gift.
  const [bursts, setBursts] = useState([]);

  const sinceRef = useRef(new Date().toISOString());
  const seenRef = useRef(new Set());
  const laneRef = useRef(0);
  const timersRef = useRef({}); // key -> timeout handle

  const scheduleClear = (key) => {
    if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
    timersRef.current[key] = setTimeout(() => {
      setCombos((cur) => {
        const next = { ...cur };
        delete next[key];
        return next;
      });
      delete timersRef.current[key];
    }, COMBO_WINDOW_MS);
  };

  // Shared dedup + combo/burst pipeline for BOTH the 1.2s poll AND the
  // Stage 4(c-plus) `gift:sent` WS event. `seenRef` guarantees at-most-once
  // rendering across the two delivery paths.
  const applyGifts = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const fresh = [];
    for (const g of items) {
      if (!g || !g.id) continue;
      if (seenRef.current.has(g.id)) continue;
      seenRef.current.add(g.id);
      fresh.push(g);
      if (g.created_at > sinceRef.current) sinceRef.current = g.created_at;
    }
    if (fresh.length === 0) return;

    setCombos((cur) => {
      const next = { ...cur };
      for (const g of fresh) {
        const senderRef = g.from_id || g.sender_id || g.sender_display_name || "anon";
        const key = `${senderRef}|${g.gift_key || g.emoji}`;
        const existing = next[key];
        if (existing) {
          next[key] = {
            ...existing,
            count: existing.count + 1,
            coins: existing.coins + (g.coins || 0),
            lastAt: Date.now(),
          };
        } else {
          next[key] = {
            key,
            count: 1,
            coins: g.coins || 0,
            emoji: g.emoji || "🎁",
            label: g.label || "gift",
            sender_handle: senderRef,
            sender_name: g.sender_display_name || "Panda Member",
            lane: laneRef.current++ % 3,
            lastAt: Date.now(),
          };
        }
        scheduleClear(key);
      }
      return next;
    });

    const burstsToAdd = fresh.slice(-3).map((g) => {
      const effect = fireGiftEffect(g.category || "classic", { silent: silentRef.current });
      return {
        id: `${g.id}-burst`,
        emoji: g.emoji || "🎁",
        giftKey: g.gift_key || null,
        anim: effect.anim,
        scale: effect.scale,
      };
    });
    setBursts((cur) => [...cur, ...burstsToAdd]);
    burstsToAdd.forEach((b) => {
      setTimeout(() => {
        setBursts((cur) => cur.filter((x) => x.id !== b.id));
      }, 1400);
    });
  }, []);

  useEffect(() => () => {
    Object.values(timersRef.current).forEach((h) => clearTimeout(h));
    timersRef.current = {};
  }, []);

  // Real-time gift delivery on the shared room socket (no polling).
  const socket = useLiveRoomSocket();
  useEffect(() => socket.subscribe("gift:sent", (m) => { if (m?.gift) applyGifts([m.gift]); }), [socket, applyGifts]);

  const comboList = Object.values(combos).sort((a, b) => a.lane - b.lane);

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden z-40"
      data-testid="live-gift-overlay"
    >
      <style>{`
        @keyframes pandax-gift-burst {
          0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          25%  { transform: translate(-50%, -50%) scale(2.4); opacity: 1; }
          60%  { transform: translate(-50%, -50%) scale(1.8); opacity: 0.9; }
          100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
        }
        /* Buzz: rapid shake — sells the vibration. */
        @keyframes pandax-gift-buzz {
          0%   { transform: translate(-50%, -50%) scale(0.6) rotate(0deg); opacity: 0; }
          10%  { transform: translate(-50%, -50%) scale(1.6) rotate(-8deg); opacity: 1; }
          20%  { transform: translate(-46%, -50%) scale(1.6) rotate(8deg); }
          30%  { transform: translate(-54%, -50%) scale(1.6) rotate(-6deg); }
          40%  { transform: translate(-46%, -50%) scale(1.6) rotate(6deg); }
          50%  { transform: translate(-52%, -50%) scale(1.6) rotate(-4deg); }
          60%  { transform: translate(-50%, -50%) scale(1.6) rotate(4deg); }
          80%  { transform: translate(-50%, -50%) scale(1.5) rotate(0deg); opacity: 0.9; }
          100% { transform: translate(-50%, -50%) scale(1.2) rotate(0deg); opacity: 0; }
        }
        /* Live-menu action: playful bounce + micro rotate. */
        @keyframes pandax-gift-pop {
          0%   { transform: translate(-50%, -30%) scale(0.4) rotate(-12deg); opacity: 0; }
          40%  { transform: translate(-50%, -55%) scale(2.0) rotate(8deg);   opacity: 1; }
          70%  { transform: translate(-50%, -50%) scale(1.7) rotate(-4deg);  opacity: 0.95; }
          100% { transform: translate(-50%, -50%) scale(1.4) rotate(0deg);   opacity: 0; }
        }
        /* DM deliverable: shimmer — inbox-style glow. */
        @keyframes pandax-gift-shimmer {
          0%   { transform: translate(-50%, -50%) scale(0.3); opacity: 0; filter: drop-shadow(0 0 0 rgba(244,63,94,0)); }
          30%  { transform: translate(-50%, -50%) scale(2.2); opacity: 1; filter: drop-shadow(0 0 40px rgba(244,114,182,0.9)); }
          60%  { transform: translate(-50%, -50%) scale(1.8); opacity: 0.95; filter: drop-shadow(0 0 30px rgba(255,205,86,0.8)); }
          100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; filter: drop-shadow(0 0 0 rgba(255,205,86,0)); }
        }
        @keyframes pandax-combo-in {
          0%   { transform: translateX(60px) scale(0.6); opacity: 0; }
          60%  { transform: translateX(-6px) scale(1.08); opacity: 1; }
          100% { transform: translateX(0) scale(1); opacity: 1; }
        }
        @keyframes pandax-count-pop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.6); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* Center emoji bursts — animation is picked per-category so buzzes
          shake, action gifts bounce, DM gifts shimmer, and classics burst.
          For the 6 flagship classic gifts (rose/fire/diamond/crown/rocket/panda)
          we swap the plain emoji for the full SVG+CSS animation. */}
      {bursts.map((b) => {
        if (isAnimatedGift(b.giftKey)) {
          return (
            <div
              key={b.id}
              className="absolute left-1/2 top-1/2"
              style={{ transform: "translate(-50%, -50%)" }}
              data-testid={`live-gift-anim-${b.giftKey}`}
            >
              <GiftAnimation giftKey={b.giftKey} size={220} />
            </div>
          );
        }
        return (
          <div
            key={b.id}
            className="absolute left-1/2 top-1/2 text-8xl drop-shadow-[0_0_40px_rgba(255,193,7,0.6)]"
            style={{
              animation: `${b.anim || "pandax-gift-burst"} 1.4s ease-out forwards`,
              transformOrigin: "center center",
            }}
          >
            {b.emoji}
          </div>
        );
      })}

      {/* Combo cards, right-side stack */}
      {comboList.map((c) => {
        const bottom = 96 + c.lane * 72;
        return (
          <div
            key={c.key}
            data-testid={`live-combo-${c.sender_handle}`}
            className="absolute right-3"
            style={{
              bottom: `${bottom}px`,
              animation: "pandax-combo-in 400ms ease-out both",
            }}
          >
            <div className="flex items-center gap-3 bg-black/60 backdrop-blur-md pl-2 pr-4 py-2 rounded-full border border-yellow-300/40 shadow-lg shadow-yellow-500/10">
              <div className="text-3xl leading-none">{c.emoji}</div>
              <div className="text-xs">
                <UserBadge
                  userRef={c.sender_handle}
                  as="span"
                  className="text-white font-semibold truncate max-w-[160px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 rounded-sm block"
                  data-testid={`live-gift-sender-${c.sender_handle}`}
                >
                  {c.sender_name}
                </UserBadge>
                <div className="text-yellow-300 text-[10px] font-medium">
                  {c.label} · {c.coins} coins
                </div>
              </div>
              {c.count > 1 && (
                <div
                  key={c.count}
                  data-testid={`live-combo-count-${c.sender_handle}`}
                  className="font-display text-3xl leading-none bg-gradient-to-b from-yellow-300 to-rose-500 bg-clip-text text-transparent drop-shadow"
                  style={{ animation: "pandax-count-pop 260ms ease-out both" }}
                >
                  ×{c.count}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
